import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { X, Plus, Minus } from "lucide-react";
import {
	ModifierGroupWithModifiers,
	SelectedModifier,
	ProductItemWithLocation,
} from "@/types/pos";
import { useSechPrice } from "@/hooks/pos/use-sech-price";
import { convertUsdToSech } from "@/lib/pos/sech-utils";
import placeholder from "@/assets/pos/placeholder.png";

interface ModifierSelectionModalProps {
	isOpen: boolean;
	onClose: () => void;
	product: ProductItemWithLocation;
	onConfirm: (
		selectedModifiers: SelectedModifier[],
		totalPriceAdjustment: number,
		quantity: number
	) => void;
	initialQuantity?: number;
}

export default function ModifierSelectionModal({
	isOpen,
	onClose,
	product,
	onConfirm,
	initialQuantity = 1,
}: ModifierSelectionModalProps) {
	const { sechPrice } = useSechPrice();
	const [quantity, setQuantity] = useState(initialQuantity);
	const [selections, setSelections] = useState<Record<number, number[]>>({});

	const modifierGroups = product.modifierGroups || [];

	// Initialize selections with defaults
	useEffect(() => {
		if (isOpen) {
			const initialSelections: Record<number, number[]> = {};
			modifierGroups.forEach((group) => {
				const defaultModifiers = group.modifiers.filter((m) => m.isDefault);
				if (defaultModifiers.length > 0) {
					initialSelections[group.id] = defaultModifiers.map((m) => m.id);
				} else if (group.required && group.modifiers.length > 0) {
					// If required and no defaults, select the first one for single selection
					if (group.selectionType === "single") {
						initialSelections[group.id] = [group.modifiers[0].id];
					}
				} else {
					initialSelections[group.id] = [];
				}
			});
			setSelections(initialSelections);
			setQuantity(initialQuantity);
		}
	}, [isOpen, product.id]);

	const handleSingleSelection = (groupId: number, modifierId: number) => {
		setSelections((prev) => ({
			...prev,
			[groupId]: [modifierId],
		}));
	};

	const handleMultipleSelection = (
		groupId: number,
		modifierId: number,
		checked: boolean
	) => {
		setSelections((prev) => {
			const current = prev[groupId] || [];
			if (checked) {
				return { ...prev, [groupId]: [...current, modifierId] };
			} else {
				return { ...prev, [groupId]: current.filter((id) => id !== modifierId) };
			}
		});
	};

	// Validate selections
	const validationErrors = useMemo(() => {
		const errors: string[] = [];
		modifierGroups.forEach((group) => {
			const selected = selections[group.id] || [];
			if (group.required && selected.length === 0) {
				errors.push(`${group.displayName} is required`);
			}
			if (group.minSelections && selected.length < group.minSelections) {
				errors.push(
					`Select at least ${group.minSelections} option(s) for ${group.displayName}`
				);
			}
			if (group.maxSelections && selected.length > group.maxSelections) {
				errors.push(
					`Select at most ${group.maxSelections} option(s) for ${group.displayName}`
				);
			}
		});
		return errors;
	}, [selections, modifierGroups]);

	const isValid = validationErrors.length === 0;

	// Calculate total price adjustment
	const totalPriceAdjustment = useMemo(() => {
		let total = 0;
		modifierGroups.forEach((group) => {
			const selectedIds = selections[group.id] || [];
			group.modifiers.forEach((modifier) => {
				if (selectedIds.includes(modifier.id)) {
					total += Number(modifier.priceAdjustmentUsd);
				}
			});
		});
		return total;
	}, [selections, modifierGroups]);

	const basePrice = product.priceUsd;
	const totalUnitPrice = basePrice + totalPriceAdjustment;
	const totalPrice = totalUnitPrice * quantity;

	const handleConfirm = () => {
		const selectedModifiers: SelectedModifier[] = [];
		modifierGroups.forEach((group) => {
			const selectedIds = selections[group.id] || [];
			group.modifiers.forEach((modifier) => {
				if (selectedIds.includes(modifier.id)) {
					selectedModifiers.push({
						modifierId: modifier.id,
						modifierGroupId: group.id,
						modifierName: modifier.displayName,
						modifierGroupName: group.displayName,
						priceAdjustmentUsd: Number(modifier.priceAdjustmentUsd),
					});
				}
			});
		});
		onConfirm(selectedModifiers, totalPriceAdjustment, quantity);
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
			<div className="bg-[var(--pos-secondary-background-color)] w-full max-w-lg rounded-2xl max-h-[90vh] flex flex-col">
				{/* Header */}
				<div className="flex items-center justify-between p-4 border-b border-[var(--pos-layout-border-color)]">
					<p className="font-montserrat text-lg leading-5 text-[var(--pos-primary-text-color)] font-bold">
						Customize Order
					</p>
					<button
						className="h-8 w-8 rounded-full flex items-center justify-center bg-[var(--pos-primary-background-color)] hover:bg-[var(--pos-layout-border-color)] transition-colors"
						onClick={onClose}
					>
						<X className="w-5 h-5 text-[var(--pos-primary-text-color)]" />
					</button>
				</div>

				{/* Product Info */}
				<div className="p-4 flex gap-4 border-b border-[var(--pos-layout-border-color)]">
					<img
						src={product.imageUrl || placeholder}
						alt={product.name}
						className="w-20 h-20 rounded-lg object-cover"
						onError={(e) => {
							e.currentTarget.src = placeholder;
						}}
					/>
					<div className="flex-1">
						<h3 className="font-montserrat text-base font-semibold text-[var(--pos-primary-text-color)]">
							{product.name}
						</h3>
						<p className="text-sm text-[var(--pos-secondary-text-color)] line-clamp-2">
							{product.description}
						</p>
						<div className="flex items-center gap-2 mt-1">
							<span className="font-figtree text-sm font-bold text-[var(--pos-primary-text-color)]">
								${Number(basePrice).toFixed(2)}
							</span>
							{sechPrice && (
								<span className="text-xs text-[#258750] font-medium">
									{convertUsdToSech(basePrice, sechPrice).toFixed(2)} SECH
								</span>
							)}
						</div>
					</div>
				</div>

				{/* Modifier Groups */}
				<div className="flex-1 overflow-y-auto p-4 space-y-4">
					{modifierGroups.map((group) => (
						<div
							key={group.id}
							className="bg-[var(--pos-primary-background-color)] rounded-lg p-4"
						>
							<div className="flex items-center justify-between mb-3">
								<div>
									<h4 className="font-montserrat text-sm font-semibold text-[var(--pos-primary-text-color)]">
										{group.displayName}
										{group.required && (
											<span className="text-[var(--pos-primary-color)] ml-1">*</span>
										)}
									</h4>
									{group.description && (
										<p className="text-xs text-[var(--pos-secondary-text-color)]">
											{group.description}
										</p>
									)}
								</div>
								<span className="text-xs text-[var(--pos-secondary-text-color)]">
									{group.selectionType === "single"
										? "Select one"
										: group.maxSelections
										? `Select up to ${group.maxSelections}`
										: "Select any"}
								</span>
							</div>

							{group.selectionType === "single" ? (
								<RadioGroup
									value={String(selections[group.id]?.[0] || "")}
									onValueChange={(value) =>
										handleSingleSelection(group.id, parseInt(value))
									}
									className="space-y-2"
								>
									{group.modifiers.map((modifier) => (
										<div
											key={modifier.id}
											className="flex items-center justify-between py-2 border-b border-dashed border-[var(--pos-layout-border-color)] last:border-0"
										>
											<div className="flex items-center gap-3">
												<RadioGroupItem
													value={String(modifier.id)}
													id={`modifier-${modifier.id}`}
												/>
												<Label
													htmlFor={`modifier-${modifier.id}`}
													className="font-figtree text-sm text-[var(--pos-primary-text-color)] cursor-pointer"
												>
													{modifier.displayName}
												</Label>
											</div>
											{Number(modifier.priceAdjustmentUsd) !== 0 && (
												<span
													className={`text-sm font-medium ${
														Number(modifier.priceAdjustmentUsd) > 0
															? "text-[var(--pos-primary-text-color)]"
															: "text-[#258750]"
													}`}
												>
													{Number(modifier.priceAdjustmentUsd) > 0 ? "+" : ""}$
													{Number(modifier.priceAdjustmentUsd).toFixed(2)}
												</span>
											)}
										</div>
									))}
								</RadioGroup>
							) : (
								<div className="space-y-2">
									{group.modifiers.map((modifier) => (
										<div
											key={modifier.id}
											className="flex items-center justify-between py-2 border-b border-dashed border-[var(--pos-layout-border-color)] last:border-0"
										>
											<div className="flex items-center gap-3">
												<Checkbox
													id={`modifier-${modifier.id}`}
													checked={(selections[group.id] || []).includes(
														modifier.id
													)}
													onCheckedChange={(checked) =>
														handleMultipleSelection(
															group.id,
															modifier.id,
															checked as boolean
														)
													}
												/>
												<Label
													htmlFor={`modifier-${modifier.id}`}
													className="font-figtree text-sm text-[var(--pos-primary-text-color)] cursor-pointer"
												>
													{modifier.displayName}
												</Label>
											</div>
											{Number(modifier.priceAdjustmentUsd) !== 0 && (
												<span
													className={`text-sm font-medium ${
														Number(modifier.priceAdjustmentUsd) > 0
															? "text-[var(--pos-primary-text-color)]"
															: "text-[#258750]"
													}`}
												>
													{Number(modifier.priceAdjustmentUsd) > 0 ? "+" : ""}$
													{Number(modifier.priceAdjustmentUsd).toFixed(2)}
												</span>
											)}
										</div>
									))}
								</div>
							)}
						</div>
					))}
				</div>

				{/* Validation Errors */}
				{validationErrors.length > 0 && (
					<div className="px-4 py-2">
						{validationErrors.map((error, index) => (
							<p key={index} className="text-xs text-[var(--pos-primary-color)]">
								{error}
							</p>
						))}
					</div>
				)}

				{/* Footer */}
				<div className="p-4 border-t border-[var(--pos-layout-border-color)] bg-[var(--pos-primary-background-color)]">
					{/* Quantity Selector */}
					<div className="flex items-center justify-between mb-4">
						<span className="font-montserrat text-sm font-medium text-[var(--pos-primary-text-color)]">
							Quantity
						</span>
						<div className="flex items-center gap-4">
							<button
								onClick={() => setQuantity(Math.max(1, quantity - 1))}
								className="h-8 w-8 rounded-full flex items-center justify-center bg-[var(--pos-secondary-background-color)]"
							>
								<Minus className="w-4 h-4 text-[var(--pos-primary-text-color)]" />
							</button>
							<span className="font-montserrat text-lg font-semibold text-[var(--pos-primary-text-color)] w-8 text-center">
								{quantity}
							</span>
							<button
								onClick={() => setQuantity(quantity + 1)}
								className="h-8 w-8 rounded-full flex items-center justify-center bg-[var(--pos-secondary-background-color)]"
							>
								<Plus className="w-4 h-4 text-[var(--pos-primary-text-color)]" />
							</button>
						</div>
					</div>

					{/* Total and Add Button */}
					<div className="flex items-center justify-between">
						<div>
							<span className="font-figtree text-lg font-bold text-[var(--pos-primary-text-color)]">
								${totalPrice.toFixed(2)}
							</span>
							{sechPrice && (
								<span className="text-sm text-[#258750] font-medium ml-2">
									{convertUsdToSech(totalPrice, sechPrice).toFixed(2)} SECH
								</span>
							)}
						</div>
						<Button
							onClick={handleConfirm}
							disabled={!isValid}
							className="bg-[var(--pos-primary-color)] text-white font-figtree font-semibold px-6 h-10 rounded-lg"
						>
							Add to Order
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}
